/*
 * Copyright (C) 2019 - Potentially Ltd
 *
 * Please see distribution for license.
 */
import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { AuthGuard } from '../shared/guards/auth.guard';

import { LoginFormComponent } from '../user-auth/components/login-form/login-form.component';
import { SignupFormComponent } from '../user-auth/components/signup-form/signup-form.component';
// eslint-disable-next-line max-len
import { AccountVerificationComponent } from '../user-auth/components/account-verification/account-verification.component';

import { NgxsLoggerPluginModule } from '@ngxs/logger-plugin';
import { NgxsModule } from '@ngxs/store';

import { MatLegacyButtonModule as MatButtonModule } from '@angular/material/legacy-button';
import { MatLegacyCardModule as MatCardModule } from '@angular/material/legacy-card';
import { SharedModule } from '../shared/shared.module';
import { UserAuthModule } from '../user-auth/user-auth.module';

import { CommonModule, ViewportScroller } from '@angular/common';
import { environment } from '../../environments/environment';
import { EnvironmentCode } from '../../environments/environment.model';
import { MainComponent } from '../index/components/main/main.component';
import { SuppressibleViewportScroller } from '../shared/services/router-scroll-suppressor.service';
import { LtiLaunchComponent } from '../user-auth/components/lti-launch/lti-launch.component';
// eslint-disable-next-line max-len
import { LtiRegistrationErrorComponent } from '../user-auth/components/lti-registration-error/lti-registration-error.component';
// eslint-disable-next-line max-len
import { EmailRecoveryComponent } from '../user-auth/components/email-recovery/email-recovery.component';
import { ForgotPasswordComponent } from '../user-auth/components/forgot-password/forgot-password.component';
import { PasswordConfirmationAndRecoveryComponent } from '../user-auth/components/password-confirmation-and-recovery/password-confirmation-and-recovery.component';
// eslint-disable-next-line max-len
import { IframeLogoutComponent } from '../user-auth/components/iframe-logout/iframe-logout.component';
import { RecoveryEmailVerificationComponent } from '../user-auth/components/recovery-email-verification/recovery-email-verification.component';
// eslint-disable-next-line max-len
import { StyleguideDemoComponent } from '@app/app/shared/styleguide/examples/styleguide-demo.component';
import { TranslocoModule, TranslocoService } from '@ngneat/transloco';
import { OrganizationLanguagesState } from '../page-modules/admin/store/languages/organization-languages-state.service';
import { PublicBadgeContentComponent } from '../page-modules/folio/components/view/public-badge-content/public-badge-content-new.component';
import { FolioPublicMomentContentComponent } from '../page-modules/folio/components/view/public-moment/public-moment-content.component';
import { FOLIO_DATA_SERVICE } from '../page-modules/folio/services/data.service';
import { CORE_MOMENT_DATA_SERVICE } from '../page-modules/folio/shared-moment-module/services/data.service';
import { momentServiceFactory } from '../page-modules/folio/shared-moment-module/shared-moment-module';
import { FolioState } from '../page-modules/folio/store/folio.state';
import { MomentUpdateViewState } from '../page-modules/folio/store/moment/moment-update/moment-update.state';
import { MomentViewAllState } from '../page-modules/folio/store/moment/moment-view-all/moment-view-all.state';
import { AllowRegistrationsGuard } from '../shared/guards/allow-registrations.guard';
import { PrearrivalsEnabledGuard } from '../shared/guards/prearrivals-enabled.guard';
import { publicFolioMatcher } from '../shared/paths/folio/folio-paths';
import { HELP_CENTER_DATA_SERVICE } from '../shared/services/help-center/help-center-data.service';
import { RestClientService } from '../shared/services/rest-client.service';
import * as SharedFactoryService from '../shared/shared-service-factory';
import { AlumniLoginComponent } from '../user-auth/components/alumni-login/alumni-login.component';
import { ClassOfLoginComponent } from '../user-auth/components/classof-auth/classof-login/classof-login.component';
import { ClassofSignupComponent } from '../user-auth/components/classof-auth/classof-singup/classof-signup.component';
import { FolioAccountVerificationComponent } from '../user-auth/components/folio-account-verification/folio-account-verification.component';
import { InvitationJoinComponent } from '../user-auth/components/invitation-join/invitation-join.component';
import { LtiDeeplinkingComponent } from '../user-auth/components/lti-deeplinking/lti-deeplinking.component';
import { LtiLaunchErrorComponent } from '../user-auth/components/lti-launch-error/lti-launch-error.component';
import { LtiPlaylistEditComponent } from '../user-auth/components/lti-playlist-edit/lti-playlist-edit.component';
import { MagicLinkSigninComponent } from '../user-auth/components/magic-link-signin/magic-link-signin.component';
import { PrearrivalsLoginFormComponent } from '../user-auth/components/prearrivals-login-form/prearrivals-login-form.component';
import { SamlSpErrorComponent } from '../user-auth/components/saml-sp/saml-sp-error/saml-sp-error.component';
import { SamlSPLaunchComponent } from '../user-auth/components/saml-sp/saml-sp.component';
import { UnverifiedEmailComponent } from '../user-auth/components/unverified-email/unverified-email.component';
import { VerifyEmailComponent } from '../user-auth/components/verify-email/verify-email.component';
import { UserAuthState } from '../user-auth/store/user-auth.state';

export const routes: Routes = [
  {
    path: 'signin',
    component: LoginFormComponent,
  },
  {
    path: 'support/signin',
    component: LoginFormComponent,
  },
  {
    path: 'external/signin',
    component: LoginFormComponent,
  },
  {
    path: 'signin/classof2020',
    component: ClassOfLoginComponent,
  },
  {
    path: 'signin/magic-link',
    component: MagicLinkSigninComponent,
  },
  {
    path: 'graduate/signin',
    component: AlumniLoginComponent,
  },
  {
    path: 'signup',
    component: SignupFormComponent,
    canActivate: [AllowRegistrationsGuard],
  },
  {
    path: 'invitation/:invitationId/accept',
    component: SignupFormComponent,
  },
  {
    path: 'invitation/:invitationId/join',
    component: InvitationJoinComponent,
  },
  {
    path: 'signup/classof2020',
    component: ClassofSignupComponent,
    canActivate: [AllowRegistrationsGuard],
  },
  {
    path: 'account-verification',
    component: AccountVerificationComponent,
  },
  {
    path: 'lti/launch',
    component: LtiLaunchComponent,
  },
  {
    path: 'lti/deeplink',
    component: LtiDeeplinkingComponent,
  },
  {
    path: 'saml/signin',
    component: SamlSPLaunchComponent,
  },
  {
    path: 'saml/signin-error',
    component: SamlSpErrorComponent,
  },
  {
    path: 'lti/playlists/edit',
    component: LtiPlaylistEditComponent,
  },
  {
    path: 'lti/registration/error',
    component: LtiRegistrationErrorComponent,
  },
  {
    path: 'lti/launch/error',
    component: LtiLaunchErrorComponent,
  },
  {
    path: 'iframe-logout',
    component: IframeLogoutComponent,
  },
  {
    path: 'reset-password',
    component: PasswordConfirmationAndRecoveryComponent,
  },
  {
    path: 'forgot-password',
    component: ForgotPasswordComponent,
  },
  {
    path: 'reset-email',
    component: EmailRecoveryComponent,
  },
  {
    path: 'confirm-email',
    component: RecoveryEmailVerificationComponent,
  },
  {
    path: 'unverified-email',
    component: UnverifiedEmailComponent,
  },
  {
    path: 'verify-email',
    component: VerifyEmailComponent,
  },
  {
    path: 'verify-personal-email',
    component: PasswordConfirmationAndRecoveryComponent,
  },
  {
    path: 'verify-alumni-email',
    component: PasswordConfirmationAndRecoveryComponent,
  },
  {
    path: 'verify-organisation-email',
    component: PasswordConfirmationAndRecoveryComponent,
  },
  {
    path: 'verify-organisation-email-with-folio',
    component: PasswordConfirmationAndRecoveryComponent,
  },
  {
    path: 'verify-folio',
    component: FolioAccountVerificationComponent,
  },
  {
    path: 'verify-alumni',
    component: FolioAccountVerificationComponent,
  },
  {
    path: 'verify-pre-arrival',
    component: FolioAccountVerificationComponent,
  },
  {
    path: 'verify-pre-arrival-with-folio',
    component: FolioAccountVerificationComponent,
  },
  {
    path: 'prearrivals/signin',
    component: PrearrivalsLoginFormComponent,
    canActivate: [PrearrivalsEnabledGuard],
  },
  {
    path: 'prearrivals/forgot-password',
    component: ForgotPasswordComponent,
    canActivate: [PrearrivalsEnabledGuard],
  },
  {
    path: 'prearrivals/signup',
    component: SignupFormComponent,
    canActivate: [AllowRegistrationsGuard, PrearrivalsEnabledGuard],
  },
  {
    path: 'folio/badge/:userBadgeId',
    component: PublicBadgeContentComponent,
  },
  {
    path: 'folio/moment/:momentId',
    component: FolioPublicMomentContentComponent,
  },
  {
    path: 'lti',
    loadChildren: () => import('../page-modules/lti/lti.module').then((m) => m.LtiModule),
  },
  {
    matcher: publicFolioMatcher,
    loadChildren: () => import('../page-modules/folio/folio.module').then((m) => m.FolioModule),
  },
  {
    path: 'public/portfolio',
    loadChildren: () => import('../page-modules/portfolio/portfolio.module').then((m) => m.PortfolioModule),
  },
  {
    path: 'folio/:folioPublicId/public/projects',
    loadChildren: () => import('../page-modules/project/project.module').then((m) => m.ProjectModule),
  },
  {
    path: 'two-factor-setup',
    loadChildren: () => import('../page-modules/two-factor-setup/two-factor-setup.module').then((m) => m.TwoFactorSetupModule),
    canActivate: [AuthGuard],
  },
  {
    path: '',
    loadChildren: () => import('../app-frame/app-frame.module').then((m) => m.AppFrameModule),
    canActivate: [AuthGuard],
  },
];

const routesToAdd: Routes =
  environment.env === EnvironmentCode.dev
    ? [
        {
          path: 'dev/styleguide',
          component: StyleguideDemoComponent,
        },
        ...routes,
      ]
    : routes;

@NgModule({
  declarations: [MainComponent],
  imports: [
    NgxsModule.forFeature([FolioState, MomentViewAllState, MomentUpdateViewState, UserAuthState, OrganizationLanguagesState]),
    UserAuthModule,
    SharedModule,
    MatButtonModule,
    MatCardModule,
    CommonModule,
    RouterModule.forRoot(routesToAdd, {
      paramsInheritanceStrategy: 'always',
      scrollPositionRestoration: 'enabled',
    }),
    NgxsLoggerPluginModule.forRoot({ disabled: environment.env === EnvironmentCode.prod }),
    TranslocoModule,
  ],
  providers: [
    AuthGuard,
    { provide: ViewportScroller, useClass: SuppressibleViewportScroller },
    {
      provide: FOLIO_DATA_SERVICE,
      useFactory: SharedFactoryService.folioServiceFactory,
      deps: [RestClientService, TranslocoService],
    },
    {
      provide: CORE_MOMENT_DATA_SERVICE,
      useFactory: momentServiceFactory,
      deps: [RestClientService, TranslocoService],
    },
    {
      provide: HELP_CENTER_DATA_SERVICE,
      useFactory: SharedFactoryService.helpCenterServiceFactory,
      deps: [RestClientService, TranslocoService],
    },
  ],
  exports: [RouterModule],
})
export class AppRoutingModule {}
